import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { ConfigCreateOverlayProps } from '../../types';
import { useStyles } from '../../styled';
import {
  Backdrop,
  Box,
  MuiBox,
  SvgIconButton,
  DotProgress,
  Logo,
  ContainerBox,
  Grid,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { CreateVenueForm } from './CreateVenueForm';
import { CreateTicketTemplateForm } from './CreateTicketTemplateForm';
import { CreateAreaForm } from './CreateAreaForm';
import { CreateKioskForm } from './CreateKioskForm';
import { CreateConfigurationForm } from './CreateConfigurationForm';
import { CreateClientIntegrationForm } from './CreateClientIntegrationForm';
import { CreateKioskHardwareForm } from './CreateKioskHardwareForm';
import { CreateHardwareForm } from './CreateHardwareForm';
import { CreateIntegrationForm } from './CreateIntegrationForm';
import { CreateKioskTicketTemplateForm } from './CreateKioskTicketTemplateForm';
import { CreateCustomerForm } from './CreateCustomerForm';
import { actions as venuesActions, selectors as venuesSelectors } from 'store/venues';
import { actions as areaActions, selectors as areaSelectors } from 'store/areas';
import { actions as ciActions, selectors as ciSelectors } from 'store/clientIntegrations';
import { actions as kiosksActions, selectors as kiosksSelectors } from 'store/kiosks';
import { actions as configurationsActions, selectors as configurationsSelectors } from 'store/configurations';
import { actions as hardwareActions, selectors as hardwareSelectors } from 'store/hardware';
import { actions as kioskHardwareActions, selectors as kioskHardwareSelectors } from 'store/kioskHardware';
import { actions as integrationActions, selectors as integrationSelectors } from 'store/integrations';
import { actions as ticketTemplatesActions, selectors as ticketTemplatesSelectors } from 'store/ticketTemplates';
import {
  actions as kioskTicketTemplateActions,
  selectors as kioskTicketTemplateSelectors
} from 'store/kioskTicketTemplates';

export const ConfigCreateOverlay = (props: ConfigCreateOverlayProps) => {
  const { kiosk, selectedCI, onCancel, configType } = props;

  // ACTION CREATORS AND SELECTORS
  const [createIntegration, resetIntegrationError, resetIntegrationAction] = useActions([
    integrationActions.createIntegration,
    integrationActions.resetError,
    integrationActions.resetAction
  ]);
  const [createVenue, resetVenueError, resetVenueAction] = useActions([
    venuesActions.createVenue,
    venuesActions.resetError,
    venuesActions.resetAction
  ]);
  const [createArea, resetAreaError, resetAreaAction] = useActions([
    areaActions.createArea,
    areaActions.resetError,
    areaActions.resetAreas
  ]);
  const [createCI, createCustomer, resetCIError, resetCIAction] = useActions([
    ciActions.createClientIntegration,
    ciActions.createCustomer,
    ciActions.resetError,
    ciActions.resetAction
  ]);
  const [createKiosk, resetKioskError, resetKioskAction] = useActions([
    kiosksActions.createKiosk,
    kiosksActions.resetError,
    kiosksActions.resetAction
  ]);
  const [createHardware, resetHardwareError, resetHardwareAction] = useActions([
    hardwareActions.createHardware,
    hardwareActions.resetError,
    hardwareActions.resetAction
  ]);
  const [createKioskTicketTemplate, resetKioskTicketTemplateError, resetKioskTicketTemplateAction] = useActions([
    kioskTicketTemplateActions.createKioskTicketTemplate,
    kioskTicketTemplateActions.resetError,
    kioskTicketTemplateActions.resetAction
  ]);
  const [createConfig, resetConfigError, resetConfigAction] = useActions([
    configurationsActions.createConfiguration,
    configurationsActions.resetError,
    configurationsActions.resetAction
  ]);
  const [createTicketTemplate, resetTicketTemplateError, resetTicketTemplateAction] = useActions([
    ticketTemplatesActions.createTicketTemplate,
    ticketTemplatesActions.resetError,
    ticketTemplatesActions.resetAction
  ]);
  const [createKioskHardware, resetKioskHardwareError, resetKioskHardwareAction] = useActions([
    kioskHardwareActions.createKioskHardware,
    kioskHardwareActions.resetError,
    kioskHardwareActions.resetAction
  ]);

  const {
    success: integrationSuccess,
    pending: integrationPending,
    error: integrationError
  } = useSelector(integrationSelectors.selectAction);
  const { success: venueSuccess, pending: venuePending, error: venueError } = useSelector(venuesSelectors.selectAction);
  const { success: areaSuccess, pending: areaPending, error: areaError } = useSelector(areaSelectors.selectAction);
  const { success: ciSuccess, pending: ciPending, error: ciError } = useSelector(ciSelectors.selectAction);
  const customer = useSelector(ciSelectors.selectCustomer);
  const { success: kioskSuccess, pending: kioskPending, error: kioskError } = useSelector(kiosksSelectors.selectAction);
  const {
    success: configSuccess,
    pending: configPending,
    error: configError
  } = useSelector(configurationsSelectors.selectAction);
  const {
    success: hardwareSuccess,
    pending: hardwarePending,
    error: hardwareError
  } = useSelector(hardwareSelectors.selectAction);
  const {
    success: kioskHardwareSuccess,
    pending: kioskHardwarePending,
    error: kioskHardwareError
  } = useSelector(kioskHardwareSelectors.selectAction);
  const {
    success: ticketTemplateSuccess,
    pending: ticketTemplatePending,
    error: ticketTemplateError
  } = useSelector(ticketTemplatesSelectors.selectAction);
  const {
    success: kioskTicketTemplateSuccess,
    pending: kioskTicketTemplatePending,
    error: kioskTicketTemplateError
  } = useSelector(kioskTicketTemplateSelectors.selectAction);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const classes = useStyles()();

  const resetError = () => {
    if (error) {
      setError('');
      if (integrationError) resetIntegrationError();
      if (venueError) resetVenueError();
      if (ciError) resetCIError();
      if (kioskError) resetKioskError();
      if (configError) resetConfigError();
      if (hardwareError) resetHardwareError();
      if (kioskHardwareError) resetKioskHardwareError();
      if (ticketTemplateError) resetTicketTemplateError();
      if (kioskTicketTemplateError) resetKioskTicketTemplateError();
      if (areaError) resetAreaError();
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (payload: any) => {
    if (configType === 'integration') createIntegration(payload);
    if (configType === 'venue') createVenue(payload);
    if (configType === 'clientIntegration') createCI(payload);
    if (configType === 'kiosk') createKiosk(payload);
    if (configType === 'configuration') createConfig(payload);
    if (configType === 'hardware') createHardware(payload);
    if (configType === 'kioskHardware') createKioskHardware(payload);
    if (configType === 'ticketTemplate') createTicketTemplate(payload);
    if (configType === 'kioskTicketTemplate') createKioskTicketTemplate(payload);
    if (configType === 'area') createArea(payload);
    if (configType === 'customer') createCustomer(payload);
  };

  useEffect(() => {
    if (
      venuePending ||
      ciPending ||
      kioskPending ||
      configPending ||
      hardwarePending ||
      kioskHardwarePending ||
      ticketTemplatePending ||
      areaPending ||
      integrationPending ||
      kioskTicketTemplatePending
    ) {
      setPending(true);
    } else {
      setPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    venuePending,
    ciPending,
    kioskPending,
    configPending,
    ticketTemplatePending,
    areaPending,
    integrationPending,
    hardwarePending,
    kioskHardwarePending,
    kioskTicketTemplatePending
  ]);

  useEffect(() => {
    if (integrationError) setError(integrationError);
    if (venueError) setError(venueError);
    if (ciError) setError(ciError);
    if (kioskError) setError(kioskError);
    if (configError) setError(configError);
    if (hardwareError) setError(hardwareError);
    if (kioskHardwareError) setError(kioskHardwareError);
    if (ticketTemplateError) setError(ticketTemplateError);
    if (kioskTicketTemplateError) setError(kioskTicketTemplateError);
    if (areaError) setError(areaError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    venueError,
    ciError,
    kioskError,
    configError,
    ticketTemplateError,
    areaError,
    integrationError,
    hardwareError,
    kioskHardwareError,
    kioskTicketTemplateError
  ]);

  useEffect(() => {
    if (venueSuccess) {
      resetVenueAction();
      onCancel();
    }
    if (areaSuccess) {
      resetAreaAction();
      onCancel();
    }
    if (ciSuccess || customer) {
      resetCIAction();
      onCancel();
    }
    if (kioskSuccess) {
      resetKioskAction();
      onCancel();
    }
    if (configSuccess) {
      resetConfigAction();
      onCancel();
    }
    if (hardwareSuccess) {
      resetHardwareAction();
      onCancel();
    }
    if (kioskHardwareSuccess) {
      resetKioskHardwareAction();
      onCancel();
    }
    if (ticketTemplateSuccess) {
      resetTicketTemplateAction();
      onCancel();
    }
    if (kioskTicketTemplateSuccess) {
      resetKioskTicketTemplateAction();
      onCancel();
    }
    if (integrationSuccess) {
      resetIntegrationAction();
      onCancel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    venueSuccess,
    ciSuccess,
    kioskSuccess,
    configSuccess,
    ticketTemplateSuccess,
    areaSuccess,
    integrationSuccess,
    hardwareSuccess,
    kioskHardwareSuccess,
    kioskTicketTemplateSuccess,
    customer
  ]);

  return (
    <>
      {pending && (
        <Backdrop open={pending ? true : false}>
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}
      <Backdrop open>
        <Box bg="light-gray" height="100vh" width="100%" overflow="auto">
          <ContainerBox className={classes.createNewClientContainer}>
            <Box bg="white" sx={{ borderRadius: '32px 32px 0 0' }} height="100%">
              <Grid className={classes.createNewClientLogoBlock}>
                <MuiBox className={classes.createNewClientLogo}>
                  <Logo />
                </MuiBox>
                <MuiBox className={classes.createNewClientCloseIcon}>
                  <SvgIconButton appearance="transparent" onClick={onCancel}>
                    <SvgIcon name="exit_circle" />
                  </SvgIconButton>
                </MuiBox>
              </Grid>
              {configType === 'integration' && (
                <CreateIntegrationForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'venue' && (
                <CreateVenueForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'ticketTemplate' && (
                <CreateTicketTemplateForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'area' && (
                <CreateAreaForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'clientIntegration' && (
                <CreateClientIntegrationForm
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
              {configType === 'kiosk' && (
                <CreateKioskForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'configuration' && (
                <CreateConfigurationForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'hardware' && (
                <CreateHardwareForm loading={pending} error={error} resetError={resetError} onSubmit={onSubmit} />
              )}
              {configType === 'kioskHardware' && (
                <CreateKioskHardwareForm
                  kiosk={kiosk}
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
              {configType === 'kioskTicketTemplate' && (
                <CreateKioskTicketTemplateForm
                  kiosk={kiosk}
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
              {configType === 'customer' && (
                <CreateCustomerForm
                  selectedCI={selectedCI}
                  loading={pending}
                  error={error}
                  resetError={resetError}
                  onSubmit={onSubmit}
                />
              )}
            </Box>
          </ContainerBox>
        </Box>
      </Backdrop>
    </>
  );
};
